import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { geoApi, type Cidade } from '@/services/geo';

import styles from './Cidades.module.css';

/**
 * Aviso honesto da casa. Estilo inline (só tokens, sem cor literal) porque o
 * módulo CSS desta tela não tem classe de aviso — mesmo padrão de
 * ConfigQuentes.tsx.
 */
const AVISO: React.CSSProperties = {
  margin: '0 0 var(--sp-4)',
  padding: 'var(--sp-2) var(--sp-3)',
  background: 'var(--bg-2)',
  borderLeft: '2px solid var(--warn)',
  borderRadius: 'var(--r-sm)',
  color: 'var(--text-3)',
  fontSize: 'var(--fs-xs)',
  lineHeight: 'var(--lh-normal)',
  maxWidth: '78ch',
};

// ordem geográfica aproximada (Norte -> Sul) para a barra de estados
const UF_ORDER = [
  'AC',
  'AP',
  'AM',
  'PA',
  'RO',
  'RR',
  'TO',
  'AL',
  'BA',
  'CE',
  'MA',
  'PB',
  'PE',
  'PI',
  'RN',
  'SE',
  'DF',
  'GO',
  'MT',
  'MS',
  'ES',
  'MG',
  'RJ',
  'SP',
  'PR',
  'RS',
  'SC',
];

function pesoClass(peso: string): string {
  if (peso === 'alta') return styles.pesoAlta;
  if (peso === 'media') return styles.pesoMedia;
  return styles.pesoEmergente;
}

function pesoLabel(peso: string): string {
  if (peso === 'alta') return 'alta';
  if (peso === 'media') return 'média';
  return 'emergente';
}

export function Cidades() {
  const [uf, setUf] = useState('SP');

  const { data, isLoading } = useQuery({
    queryKey: ['geo', 'cidades'],
    queryFn: geoApi.getCidades,
  });

  const ufList = useMemo(() => {
    if (!data) return [];
    const known = Object.keys(data.estados);
    const ordered = UF_ORDER.filter((u) => known.includes(u));
    const rest = known.filter((u) => !UF_ORDER.includes(u));
    return [...ordered, ...rest];
  }, [data]);

  const cidades: Cidade[] = data?.estados[uf] ?? [];
  const totalCidades = useMemo(
    () => (data ? Object.values(data.estados).reduce((s, cs) => s + cs.length, 0) : 0),
    [data],
  );

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Cidades por estado</h1>
          <p className={styles.subtitle}>
            Curadoria jornalística: que cidades de cada estado geram notícia, pesando o financeiro,
            o cultural e o populacional. Contagem variável — hoje ela é a base do sombreado do mapa
            do Brasil.
          </p>
        </div>
        <span className={styles.total}>
          {isLoading ? 'carregando…' : `${ufList.length} estados · ${totalCidades} cidades`}
        </span>
      </header>

      {/* A curadoria alimenta MESMO o sombreado do mapa (geo.py → cidades). O
          que não existe é o elo com a fastnews: quentes_flow passa só a string
          `geo` (BR-UF) para a busca — nome, peso e fatores não chegam à
          seleção nem ao texto. Chamar a curadoria de alicerce da esteira
          automática era promessa sem cadeia. */}
      <p role="status" style={AVISO}>
        A fastnews por estado ainda não lê esta curadoria (em construção): ela roda por lote manual
        — no Mapa do Brasil, &quot;Rodar fastnews (lote)&quot; e depois &quot;Confirmar&quot; — e
        busca apenas pela geografia do estado. Nome da cidade, peso e fatores não entram na busca
        nem no texto.
      </p>

      <div className={styles.layout}>
        <nav className={styles.rail} aria-label="Estados">
          {ufList.map((u) => {
            const r = data?.resumo[u];
            return (
              <button
                key={u}
                type="button"
                className={`${styles.railItem} ${u === uf ? styles.railActive : ''}`}
                onClick={() => setUf(u)}
              >
                <span className={styles.railUf}>{u}</span>
                <span className={styles.railName}>{r?.nome ?? u}</span>
                <span className={styles.railCount}>{r?.total ?? 0}</span>
              </button>
            );
          })}
        </nav>

        <div className={styles.panel}>
          <div className={styles.panelHead}>
            <h2 className={styles.panelTitle}>{data?.uf_nomes[uf] ?? uf}</h2>
            <span className={styles.panelCount}>{cidades.length} cidades</span>
          </div>

          <div className={styles.grid}>
            {cidades.map((c) => (
              <div key={c.nome} className={styles.card}>
                <div className={styles.cardTop}>
                  <span className={styles.cardName}>{c.nome}</span>
                  <span className={`${styles.peso} ${pesoClass(c.peso)}`}>{pesoLabel(c.peso)}</span>
                </div>
                <p className={styles.motivo}>{c.motivo}</p>
                <div className={styles.fatores}>
                  {c.fatores.map((f) => (
                    <span key={f} className={styles.fator}>
                      {f}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
